import { isHostDeclaredTool, toHostDeclaredTool } from "./host-declaration";
import type { AnyMagnusClientTool, MagnusClientTool } from "./types";

/**
 * Define a client-side tool in one place: input schema, host-side
 * execution, and (optionally) its generative UI view.
 *
 * Pass the result to `<MagnusProvider tools={[...]}>`; the SDK splits it
 * back into an executor (run on tool call, result POSTed to the backend)
 * and a display-registry entry (`<ToolRenderer>` resolves the view).
 *
 * A tool that provides both `description` and `inputSchema` is
 * **host-declared** (ADR-096): the SDK sends them to Magnus so the model can
 * call it. Write a plain snake_case name — behind the scenes the model sees
 * it namespaced as `{appSurface}__{name}`, which is what makes collisions
 * with Magnus's own tools impossible; your code never deals with that form.
 * Host-declared tools are validated eagerly here — a bad declaration throws
 * at module scope, in your build, rather than failing silently three layers
 * downstream.
 *
 * @example
 * // Server-declared executor (schema lives in a Magnus capability, so no
 * // description/inputSchema here):
 * export const getSheetNames = defineClientTool({
 *   name: "getSheetNames",
 *   execute: async () => Excel.run(...),
 *   display: { kind: "step", category: "create", view: lazy(() => import("./view")) },
 * });
 *
 * @example
 * // Host-declared tool (your app's own verb — the model sees this schema):
 * export const navigateInApp = defineClientTool({
 *   name: "navigate_in_app",
 *   description: "Navigate the app to one of its sections.",
 *   inputSchema: z.object({ destination: z.enum(["home", "projects"]) }),
 *   execute: ({ destination }) => router.push(destination),
 * });
 */
export function defineClientTool<TInput, TView = unknown>(
  tool: MagnusClientTool<TInput, TView>
): MagnusClientTool<TInput, TView> {
  if (isHostDeclaredTool(tool as AnyMagnusClientTool)) {
    // Validate + convert, discarding the result — the throw is the point.
    toHostDeclaredTool(tool as AnyMagnusClientTool);
  }
  return tool;
}
